import { ChangeDetectorRef, Component, Output, EventEmitter, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PaymentService } from '../../services/payments/payment/payment.service';
import { PackagesService } from '../../services/payments/packages/packages.service';
import { PackagesResponse } from '../../models/payment/packagesResponse';
import { CreatePayNowRequest } from '../../models/payment/createPayNowRequest';
import { PaymentGatewayResponse } from '../../models/payment/paymentGatewayResponse.model';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { UserSelectors } from '../../states/user/user-selector';
import { StudentDirectApplicationService } from '../../services/student-direct-application/student-direct-application.service';
import { DirectApplyCheckoutService } from '../../services/payments/direct-apply-checkout/direct-apply-checkout.service';
import { InstituteService } from '../../services/institute/institute.service';
import { catchError, filter, of, take } from 'rxjs';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { BlockUIModule } from 'primeng/blockui';
import { ButtonModule } from 'primeng/button';
import { AuthService } from '../../services/account/auth.service';
import { UserResponse } from '../../models/account/userResponse';
import { ContactPurpose } from '../../models/enums/contactUs';
import { ClarityCallPopupComponent } from '../../shared/components/clarity-call-popup/clarity-call-popup.component';
export interface DirectApplyReviewItem {
  id: number;
  instituteId: string;
  instituteName: string;
  courseName?: string | null;
  courseLevelName?: string | null;  // New field from updated DTO
  inTakeYear?: string | null;
  mentorDeadline?: string | null;   // Enriched from MyInstitutes, not in DTO
  logo?: string;                    // Enriched from institute search, not in DTO
  courseLevel?: number | null;
  courseId?: number | null;
  status?: number;                  // New field from updated DTO
  milestones?: any[];               // New field from updated DTO
}

@Component({
  selector: 'app-review',
  standalone: true,
  imports: [CommonModule, ToastModule, BlockUIModule, ButtonModule, ClarityCallPopupComponent],
  providers: [MessageService],
  templateUrl: './review.component.html',
  styleUrl: './review.component.scss'
})
export class ReviewComponent implements OnInit {
  @Output() onNext = new EventEmitter<void>();
  @Output() loaded = new EventEmitter<void>();

  packageDetails: PackagesResponse | null = null;
  loading: boolean = true;
  isSaving: boolean = false;
  paymentInProgress: boolean = false;
  // Direct apply colleges from API
  directApplyColleges: DirectApplyReviewItem[] = [];
  isDirectApplyLoading = true;
  user: any;
  showClarityPopup = false;
 ContactPurpose = ContactPurpose;

  get baseAmount(): number {
    const perCollegeAmount = this.packageDetails?.amount || 1000;
    return perCollegeAmount * this.selectedSlots;
  }

  get gstAmount(): number {
    return +(this.baseAmount * 0.18).toFixed(2);
  }

  get totalAmount(): number {
    return this.baseAmount + this.gstAmount;
  }

  get totalSlots(): number { return 5; }
  get selectedSlots(): number { return this.directApplyColleges.length; }
  get freeSlots(): number { return Math.max(0, this.totalSlots - this.selectedSlots); }

  constructor(
    private paymentService: PaymentService,
    private packagesService: PackagesService,
    private route: ActivatedRoute,
    private router: Router,
    private store: Store,
    private studentDirectApplicationService: StudentDirectApplicationService,
    private checkoutService: DirectApplyCheckoutService,
    private instituteService: InstituteService,
    private cdr: ChangeDetectorRef,
    private messageService: MessageService,
      private authService: AuthService,
  ) { }

  ngOnInit() {
    this.packagesService.getPackageBySystemAssignedName('Institute Direct Application').subscribe({
      next: (res) => {
        this.packageDetails = res;
        this.loading = false;
        this.loaded.emit();
      },
      error: (err) => {
        console.error('Failed to load package', err);
        this.loading = false;
        this.loaded.emit();
      }
    });

    this.loadDirectApplyColleges();
  }

  /**
   * Loads the list of Direct Apply colleges for the current student,
   * using the same API pattern as my-colleges component.
   */
  loadDirectApplyColleges() {
    this.isDirectApplyLoading = true;

    this.store.select(UserSelectors.getUser).pipe(
      filter(user => !!user?.id),
      take(1)
    ).subscribe(user => {
      this.user = user;

      this.studentDirectApplicationService
        .getByStudentId({ studentId: user.id })
        .pipe(catchError(() => of(null)))
        .subscribe((response: any) => {
          let rawItems: any[] = [];

          if (Array.isArray(response)) {
            rawItems = response;
          } else if (response?.result) {
            rawItems = Array.isArray(response.result) ? response.result : [response.result];
          } else if (response && response.instituteId) {
            rawItems = [response];
          }

          this.directApplyColleges = rawItems.map((item: any): DirectApplyReviewItem => ({
            id: item.id ?? 0,
            instituteId: String(item.instituteId ?? item.id ?? ''),
            // instituteName now returned directly by API via AutoMapper (src.Institute.Name)
            instituteName: item.instituteName ?? 'Loading...',
            // courseName now returned directly by API via AutoMapper (src.Course.CourseName)
            courseName: item.courseName ?? null,
            courseLevelName: item.courseLevelName ?? null,
            inTakeYear: item.inTakeYear ?? null,
            mentorDeadline: null,  // Not in DTO; enriched separately if needed
            logo: '',              // Not in DTO; enriched from instituteService below
            courseLevel: item.courseLevel ?? null,
            courseId: item.courseId ?? null,
            status: item.status ?? 0,
            milestones: item.milestones ?? [],
          }));

          // Enrich entries that are missing instituteName / logo / courseName
          this.directApplyColleges.forEach((da, idx) => {
            const needsInstitute = da.instituteName === 'Loading...' || !da.instituteName || !da.logo;
            const needsCourse = da.courseId && !da.courseName;

            if (needsInstitute || needsCourse) {
              this.instituteService.getInstitute(da.instituteId).subscribe(inst => {
                if (inst) {
                  const resolvedName = inst.instituteName && inst.instituteName.trim() !== '' ? inst.instituteName : da.instituteName;
                  this.directApplyColleges[idx] = {
                    ...this.directApplyColleges[idx],
                    instituteName: resolvedName,
                    logo: inst.logo || da.logo,
                    courseName: da.courseName || inst.courseName || null,
                  };
                  this.cdr.markForCheck();

                  // Now that we have the resolved name, search for the course
                  if (needsCourse && resolvedName && resolvedName !== 'Loading...') {
                    const req = {
                      name: resolvedName,
                      pageNumber: 1,
                      pageSize: 500
                    };
                    this.instituteService.searchCourses(req).subscribe(res => {
                      const course = res.result?.find((c: any) => Number(c.id) === Number(da.courseId));
                      if (course) {
                        this.directApplyColleges[idx] = {
                          ...this.directApplyColleges[idx],
                          courseName: course.courseName
                        };
                        this.cdr.markForCheck();
                      }
                    });
                  }
                }
              });
            }
          });

          this.isDirectApplyLoading = false;
          this.cdr.markForCheck();
        });
    });
  }

  /**
   * Removes a college from the Direct Apply list via DELETE API.
   */
  removeDirectApply(index: number) {
    const item = this.directApplyColleges[index];
    if (!item?.id) {
      this.directApplyColleges.splice(index, 1);
      return;
    }

    this.studentDirectApplicationService.deleteStudentDirectApplication(item.id).subscribe({
      next: () => {
        this.directApplyColleges.splice(index, 1);
        this.cdr.markForCheck();
      },
      error: (err) => {
        console.error('Failed to remove college from Direct Apply', err);
      }
    });
  }

  /** Navigate to college search so user can add more colleges */
  browseColleges() {
    this.router.navigate(['/portal', 'institute-search'], { queryParams: { tab: 'universities' } });
  }

  processPayment() {
    if (!this.packageDetails) return;
    this.paymentInProgress = true;

    // Gateway redirects back here after payment; the callback appends /{txnId}?success=...
    const sourceUrl = window.location.origin + '/portal/direct-apply/payment-review';

    this.checkoutService.initiatePayment(sourceUrl).subscribe({
      next: (response: PaymentGatewayResponse) => {
        if (response.redirectUri && response.redirectUri.trim() !== '') {
          window.location.href = response.redirectUri;
        } else {
          this.paymentInProgress = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to process payment. Please try again.'
          });
        }
      },
      error: (err: any) => {
        this.paymentInProgress = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to process payment. Please try again.'
        });
      }
    });
  }

}
